import axios from 'axios';
import { auth } from '@obis/firebase-config';
import { User } from 'firebase/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://obis-crm-backend-394529471436.us-central1.run.app/api/v1';

/**
 * Resolve the live Firebase user, waiting for auth persistence to restore on
 * a fresh page load (auth.currentUser can be null for a tick after reload).
 */
const waitForCurrentUser = (): Promise<User | null> =>
  new Promise((resolve) => {
    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });

/**
 * Shared authenticated token provider for all CRM protected requests.
 * Always returns a live Firebase ID token (auto-refreshed by the SDK) instead
 * of a stale value cached in localStorage. Returns null when no user is signed in.
 */
export const getAuthToken = async (forceRefresh = false): Promise<string | null> => {
  try {
    const user = await waitForCurrentUser();
    if (!user) return null;
    return await user.getIdToken(forceRefresh);
  } catch (error) {
    console.error('❌ Error getting auth token:', error);
    return null;
  }
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    const token = await getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('⚠️ No authenticated user found');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
